import Typography from '@mui/material/Typography';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { TrackedTranslation } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import { ServerMessageHandler } from '@swf/notification';

import useLogout from '../../../hooks/useLogout';
import { RealMoneyStatus } from '../../../types/status';
import Prompt from '../../Prompt/Prompt';
import { promptTitleCapitalize } from '../shared.scss';

import {
  TXTCLI_Account_Suspended,
  TXTCLI_Account_declined,
  TXTCLI_Logout,
  TXTCLI_We_apologize_for_the_inconveni_ELL,
  TXTCLI_Your_account_has_been_found_on_ELL,
  TXTSRV_Please_try_again_laterX,
  TXTWCL_Account_reactivated,
  TXTWCL_Account_suspended,
  TXTWCL_Any_deposits_or_withdrawals_yo_ELL,
  TXTWCL_In_accordance_with_Portuguese__ELL,
  TXTWCL_In_case_you_have_an_outstandin_ELL,
  TXTWCL_Okay,
  TXTWCL_Something_went_wrong,
  TXTWCL_There_has_been_an_issue_with_y_ELL,
  TXTWCL_We_are_temporarily_unable_to_c_ELL_1,
  TXTWCL_We_will_contact_you_once_we_ha_ELL,
  TXTWCL_You_have_been_removed_from_the_ELL,
  TXTWCL_You_will_be_able_to_create_a_n_ELL,
  TXTWCL_Your_account_has_been_temporar_ELL_2,
  TXTWCL_Your_account_privileges_have_b_ELL_1
} from './OasisErrors.messages.json';

interface Props {
  onClose(): void;
  code: RealMoneyStatus;
  defaultMessage: string;
}

interface OasisError {
  title: TrackedTranslation;
  content: JSX.Element;
}

const getTranslations = (code: RealMoneyStatus, site: Sites, defaultMessage: string) => {
  switch (code) {
    case RealMoneyStatus.USER_BANNED_BY_REGULATOR:
    case RealMoneyStatus.DE_BANNED_BY_REGULATOR:
      return {
        title: TXTCLI_Account_declined,
        content: (
          <Typography>
            <Translation {...TXTWCL_Your_account_privileges_have_b_ELL_1} />{' '}
            <Translation {...TXTCLI_We_apologize_for_the_inconveni_ELL} />
          </Typography>
        )
      };
    case RealMoneyStatus.DE_UNBANNED_BY_REGULATOR:
      return {
        title: TXTWCL_Account_reactivated,
        content: (
          <Typography>
            <Translation {...TXTWCL_You_have_been_removed_from_the_ELL} />
          </Typography>
        )
      };
    case RealMoneyStatus.BLACKLIST_SERVICE_NOT_AVAILABLE:
      return {
        title: TXTWCL_There_has_been_an_issue_with_y_ELL,
        content: (
          <Typography>
            <ServerMessageHandler message={defaultMessage} />
          </Typography>
        )
      };
    case RealMoneyStatus.DBM_ACCOUNT_CLOSED:
      if (site === Sites.POR) {
        return {
          title: TXTCLI_Account_Suspended,
          content: (
            <Typography>
              <Translation {...TXTCLI_Your_account_has_been_found_on_ELL} />{' '}
              <Translation {...TXTWCL_In_accordance_with_Portuguese__ELL} />{' '}
              <Translation {...TXTWCL_Any_deposits_or_withdrawals_yo_ELL} />{' '}
              <Translation {...TXTWCL_In_case_you_have_an_outstandin_ELL} />{' '}
              <Translation {...TXTWCL_You_will_be_able_to_create_a_n_ELL} />
            </Typography>
          )
        };
      } else {
        return {
          title: TXTWCL_Account_suspended,
          content: (
            <Typography>
              <Translation {...TXTWCL_Your_account_has_been_temporar_ELL_2} />{' '}
              <Translation {...TXTWCL_We_will_contact_you_once_we_ha_ELL} />
            </Typography>
          )
        };
      }
    default:
      return {
        title: TXTWCL_Something_went_wrong,
        content: (
          <Typography>
            <Translation {...TXTWCL_We_are_temporarily_unable_to_c_ELL_1} />{' '}
            <Translation {...TXTSRV_Please_try_again_laterX} />
          </Typography>
        )
      };
  }
};

const OasisErrors: React.FC<Props> = ({ onClose, code, defaultMessage }) => {
  const logout = useLogout();
  const siteId = useSelector(select.cms.getSiteId);
  const isPt = siteId === Sites.POR;
  const handleClose = () => {
    if (code !== RealMoneyStatus.DE_UNBANNED_BY_REGULATOR) {
      logout();
    }
    onClose();
  };

  const displayValues: OasisError = getTranslations(code, siteId, defaultMessage);

  return (
    <Prompt
      classes={{ title: promptTitleCapitalize }}
      noCloseButton={isPt}
      onClose={handleClose}
      variant="default"
      title={{ translation: displayValues.title }}
      onPrimaryButtonClick={handleClose}
      primaryButton={{ translation: isPt ? TXTCLI_Logout : TXTWCL_Okay }}
    >
      {displayValues.content}
    </Prompt>
  );
};

export default OasisErrors;
